import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Divider, Tooltip,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader, SelectDropdown, Wrapper } from '../../components';
import { ApiCatalogCards } from '../../components/api-catalog';
import { Logs, apiCategortList, postFile, postFileToVaults, getAllApisWithSubscription, digilockerService } from '../../services';
import { Combinator } from './Combinator';
import { keysPair } from '../../services/keysPairService';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import WalletIcon from '@mui/icons-material/Wallet';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { notification } from 'antd';
import AddToVaultDialog from './addToVaultDialog';
import { totalCreditsAndRemaining } from '../../services/creditsService';
import { remainingCredits } from '../../store/reducers/auth';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';


export function OnscreenApi() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [apiCategoryAndList, setApiCategoryAndList] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedAPI, setSelectedAPI] = useState("");
  const [apiListItems, setApiListItems] = useState({});
  const [status, setStatus] = useState(null);
  const [showApiList, setShowApiList] = useState(false);
  const [queryParamsFlag, setQueryParamsFlag] = useState(false);
  const [apiCredList, setApiCredList] = useState([]);
  const [selectedApiKey, setSelectedApiKey] = useState('');
  const [file, setFile] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState({
    error: null,
    uploaded: null,
    masked: null,
    status: null,
  });

  const emailsToCheck = [
    "vinod.kumawat@habilelabs.io",
    "reena@habilelabs.io",
    "babudevatwal1996@gmail.com",
  ];

  const isEmailMatch = useMemo(() => {
    return emailsToCheck?.includes(userDetails?.email);
  }, [userDetails?.email]);

  const transformApiDataToFlatList = (groupedData) => {
    console.log("✌️groupedData --->", groupedData);
    return Object.entries(groupedData).flatMap(([categoryName, categoryObj]) =>
      categoryObj.apis.map((api) => ({
        ...api,
        category: categoryName, // add category info to each API
      }))
    );
  };

  const [categoryChange, setCategoryChange] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);


  useEffect(() => {
    const callbackRaw = sessionStorage.getItem('digilocker_callback');
    const aadhaarNumber = sessionStorage.getItem('aadhaar_number');
    if (!callbackRaw || !aadhaarNumber) return;

    const clearDigilockerSession = () => {
      sessionStorage.removeItem('digilocker_callback');
      sessionStorage.removeItem('aadhaar_number');
      sessionStorage.removeItem('code_verifier');
      sessionStorage.removeItem('state');
    };

    const callback = JSON.parse(callbackRaw);
    if (Date.now() - callback.timestamp >= 2 * 60 * 1000) {
      clearDigilockerSession();
      return;
    }

    setJsonData(null);
    setDigilockerData(null);
    setDigilockerLoading(true);

    const refreshRemainingCredits = async () => {
      const remainingCredit = await totalCreditsAndRemaining();
      if (remainingCredit?.status === 200) {
        dispatch(
          remainingCredits({
            credits: remainingCredit?.data?.credit?.remainingCredit,
          }),
        );
      }
    };

    const handleDigilockerSuccess = (userData) => {
      setDigilockerData(userData);
      // Set status first, then data to ensure proper rendering
      setStatus(200);
      setTimeout(() => {
        setJsonData(userData);
      }, 100);
      setTimeout(refreshRemainingCredits, 200);
      // Store Aadhaar number for auto-fill
      sessionStorage.setItem('digilocker_aadhaar', aadhaarNumber);
    };

    const handleDigilockerError = (err) => {
      console.error('DigiLocker error:', err);
      setDigilockerError(err.message);

      let errorData;
      let statusCode = 500;

      if (err.response?.data) {
        errorData = err.response.data;
        statusCode = err.response.status;
      } else if (err.message) {
        errorData = { error: err.message };
      } else {
        errorData = { error: 'DigiLocker authentication failed' };
      }
      setStatus(statusCode);
      setTimeout(() => {
        setJsonData(errorData);
      }, 100);
    };

    digilockerService
      .getAccessToken(callback.code, sessionStorage.getItem('code_verifier'))
      .then((tokenData) => {
        const accessToken = tokenData.access_token;
        if (!accessToken) {
          throw new Error('No access token received');
        }
        return digilockerService.handleCallback(accessToken, callback.state);
      })
      .then((callbackData) => {
        console.log('DigiLocker Callback Data:', callbackData);
        return digilockerService.getUserData(aadhaarNumber);
      })
      .then(handleDigilockerSuccess)
      .catch(handleDigilockerError)
      .finally(() => {
        setDigilockerLoading(false);
        clearDigilockerSession();
      });
  }, []);

  const filterDigilockerApi = (categories = {}) =>
    Object.fromEntries(
      Object.entries(categories).map(([category, data]) => [
        category,
        {
          ...data,
          apis: (data.apis || []).filter(
            (api) => api?.name?.toLowerCase() !== "auth url digilocker"
          ),
        },
      ])
    );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getAllApisWithSubscription();
        if (res?.status === 200) {
          const rawCategories = res?.data?.data?.attributes?.apisByCategory || {};
          const filteredServices = filterDigilockerApi(rawCategories);

          setApiCategoryAndList(filteredServices);
          setApiListItems(transformApiDataToFlatList(filteredServices));

          const sortedCategories = Object.keys(filteredServices).sort();
          setSelectedCategory(sortedCategories[0] || "");
        }
      } catch (error) {
        Logs(error);
        // Fallback to original API if new one fails
        try {
          const fallbackRes = await apiCategortList();
          if (fallbackRes?.status === 200) {
            const rawCategories = fallbackRes?.data?.data?.attributes?.apisByCategory || {};

            const filteredServices = filterDigilockerApi(rawCategories);

            setApiCategoryAndList(filteredServices);
            setApiListItems(transformApiDataToFlatList(filteredServices));

            const sortedCategories = Object.keys(filteredServices).sort();
            setSelectedCategory(sortedCategories[0] || "");
          }
        } catch (fallbackError) {
          Logs(fallbackError);
        }
      }

      try {
        const response = await keysPair({ id: user?.company?.id });

        const extractApiIds = (apis) => apis?.data?.map((api) => api.id);
        const mapCredItem = (item) => ({
          id: item.id,
          apiKey: item.attributes.apiKey,
          apiSecret: item.attributes.apiSecret,
          apiAccessKeysName: item.attributes.apiAccessKeysName,
          apis: extractApiIds(item.attributes?.apis),
        });
        const data = response?.data?.data?.map(mapCredItem) || [];

        setApiCredList(data);
        // Set first API key as default
        if (data.length > 0 && !selectedApiKey) {
          setSelectedApiKey(data[0].id.toString());
        }
      } catch (error) {
        console.error("Error fetching API keys:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.company?.id]);

  useEffect(() => {
    if (
      !categoryChange &&
      selectedCategory &&
      apiCategoryAndList[selectedCategory]?.apis
    ) {
      const sortedApis = apiCategoryAndList[selectedCategory]?.apis.sort(
        (a, b) => a.name.localeCompare(b.name)
      );
      const firstApi = sortedApis[0] || null;
      if (!Array.isArray(apiCredList)) {
        console.error("apiCredList is not an array:", apiCredList);
        return;
      }
      if (firstApi?.id) {
        setSelectedAPI(firstApi.id);
        let pickedSecret = selectedApiKey
          ? apiCredList?.find(cred => cred.id === parseInt(selectedApiKey))
          : apiCredList?.find(
            (cred) =>
              Array.isArray(cred.apis) && cred?.apis?.includes(firstApi.id)
          ) || null;
        if (pickedSecret) {
          sessionStorage.setItem("apiKey", pickedSecret.apiKey);
          sessionStorage.setItem("apiSecret", pickedSecret.apiSecret);
        } else {
          sessionStorage.setItem("apiKey", "");
          sessionStorage.setItem("apiSecret", "");
        }
      } else {
        setSelectedAPI("");
        sessionStorage.setItem("apiKey", "");
        sessionStorage.setItem("apiSecret", "");
      }
    }
  }, [selectedCategory, apiCredList, categoryChange]);

  useEffect(() => {
    if (selectedAPI) {
      const pickedSecret = selectedApiKey
        ? apiCredList?.find(cred => cred.id === parseInt(selectedApiKey))
        : apiCredList?.find(
          (cred) =>
            Array.isArray(cred.apis) && cred?.apis?.includes(selectedAPI)
        ) || null;
      if (pickedSecret) {
        sessionStorage.setItem("apiKey", pickedSecret.apiKey);
        sessionStorage.setItem("apiSecret", pickedSecret.apiSecret);
      } else {
        sessionStorage.setItem("apiKey", "");
        sessionStorage.setItem("apiSecret", "");
      }
    }
  }, [selectedAPI, apiCredList, selectedApiKey]);

  const getSelectedApiInfo = (apiId) => {
    const categories = Object.values(apiCategoryAndList);
    for (const category of categories) {
      const foundApi = category.apis.find((api) => String(api.id) === String(apiId));
      if (foundApi) {
        return foundApi;
      }
    }
    return null;
  };

  const getSelectedApiId = (apiName) => {
    const categories = Object.values(apiCategoryAndList);
    for (const category of categories) {
      const foundApi = category.apis.find((api) => api.name === apiName);
      if (foundApi) {
        return foundApi.id;
      }
    }
    return null;
  };
  // Restore state from URL query parameters
  useEffect(() => {
    const category = searchParams.get('category');
    const apiId = searchParams.get('apiId');

    // Only restore if we have URL params AND the data is loaded
    if (category && apiId && apiCategoryAndList[category]?.apis?.length > 0) {
      setSelectedCategory(category);
      setSelectedAPI(apiId);
      setShowApiList(true);
      setCategoryChange(true);
    }
  }, [searchParams, apiCategoryAndList]);

  // Also handle legacy location.state for backward compatibility
  useEffect(() => {
    if (location.state && !searchParams.get('category')) {
      const { selectedCategory, selectedApi } = location.state;
      setSelectedCategory(selectedCategory);
      const apiId = getSelectedApiId(selectedApi);
      setSelectedAPI(apiId);
      setCategoryChange(true);
      // Update URL to include the query parameters
      if (apiId) {
        setSearchParams({ category: selectedCategory, apiId });
      }
    }
  }, [location.state, apiCategoryAndList, searchParams, setSearchParams]);

  const handleCategoryCardClick = (category) => {
    setSelectedCategory(category.name);
    setShowApiList(true);
    setStatus(null);
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleApiClick = (api, categoryName) => {
    setSelectedCategory(categoryName);
    setSelectedAPI(api.id);
    setShowApiList(true);
    setStatus(null);
    setFile(null);
    setCategoryChange(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Update URL with query parameters so the state persists on refresh
    setSearchParams({ category: categoryName, apiId: api.id });
    // Store in session that we came directly from an API card
    sessionStorage.setItem("directApiSelection", "true");
  };

  const handleBackToCategories = () => {
    setShowApiList(false);
    setSelectedCategory("");
    setSelectedAPI("");
    setStatus(null);
    setFile(null);
    // Clear URL query parameters when going back
    setSearchParams({});
  };

  const { api } = useMemo(() => {
    const selected = apiCategoryAndList[selectedCategory] || [];
    if (selected?.apis?.length > 0) {
      // Convert both to string for comparison to handle number/string mismatch from URL params
      const selectedApi = selected.apis.find((x) => String(x.id) === String(selectedAPI));
      const apiDetails = getSelectedApiInfo(selectedAPI);
      return { api: selectedApi, apiDetails };
    }
    return { api: null, apiDetails: null };
  }, [selectedCategory, apiCategoryAndList, selectedAPI]);

  // Set queryParamsFlag in separate useEffect to avoid infinite re-renders
  useEffect(() => {
    if (api?.apiInput?.[0]?.queryParam !== undefined) {
      setQueryParamsFlag(api.apiInput[0].queryParam);
    }
  }, [api]);

  const handleAddClick = () => {
    setOpenDialog(true);
  };

  const handleVaultClick = () => {
    navigate("/vault");
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleAddConfirm = async () => {
    try {
      const res = await postFile({
        file: file,
        number: jsonData?.detectedDetails || jsonData?.detectedFiles || null,
      });
      if (res?.status === 201) {
        notification.success({
          message: "Success",
          description: "Successfully added response to vault",
        });

        const documentName = file?.name;
        const documentType = file?.type;
        const token = res?.data?.token;
        const response =
          jsonData?.detectedDetails || jsonData?.detectedFiles || null;
        const apiName = getSelectedApiInfo(selectedAPI);
        if (token) {
          await postFileToVaults({
            token,
            documentName,
            documentType,
            response,
            apiName: apiName.name,
          });
        } else {
          console.error("No token received in response:", response);
          notification.error({
            message: "Error",
            description: "No token received in response",
          });
        }
      } else {
        console.error("Unexpected response status:", response);
        notification.error({
          message: "Error",
          description: `Unexpected response status: ${response?.status}`,
        });
      }
    } catch (error) {
      console.error("Error adding API to vault:", error);
      notification.error({
        message: "Error",
        description: "Failed to add response to vault",
      });
    } finally {
      setOpenDialog(false);
    }
  };

  let creditText = "";
  if (api?.creditConsumptionValue !== undefined) {
    const creditValue = api?.creditConsumptionValue ?? 0;
    creditText =
      creditValue > 1
        ? `${creditValue} credits per request`
        : `${creditValue} credit per request`;
  } else {
    creditText = "credit per request";
  }

  let creditPerAadhaar = "";
  if (api?.pricingPerPage !== undefined) {
    const aadhaarValue = api?.pricingPerPage ?? 0;
    creditPerAadhaar =
      aadhaarValue > 1
        ? `${aadhaarValue} credits per page`
        : `${aadhaarValue} credits per page`;
  } else {
    creditPerAadhaar = "credits per page";
  }

  return (
    <Wrapper>
      {loading && <Loader />}
      {!showApiList ? (
        // Category Cards View
        <Box sx={{ mt: { xs: 10, sm: 12, md: 12 }, mb: 2 }}>
          <ApiCatalogCards
            categories={apiListItems}
            onCategoryClick={handleCategoryCardClick}
            onApiClick={handleApiClick}
          />
        </Box>
      ) : (
        // API Selection View (Original Implementation)
        <>
          <Paper sx={{ mt: { xs: 10, sm: 12, md: 12 }, mb: 2 }}>
            <Stack
              direction="row"
              sx={{ pt: 2, px: 2, pb: "0.6rem" }}
              spacing={2}
              justifyContent="space-between"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Box
                  onClick={handleBackToCategories}
                  sx={{
                    height: 40,
                    width: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#1976d2",

                    "&:hover": {
                      opacity: 0.7,
                    },
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 25 }} />
                </Box>

                {/* <Button
                  onClick={handleBackToCategories}
                  sx={{
                    color: "#1976d2",
                    minWidth: "auto",
                    fontSize: "14px",
                    fontWeight: "400",
                    gap: "10px",
                    marginRight: "10px",
                    border: "solid 1px #1976d2",
                    flexShrink: 0, 
                    flexGrow: 0,
                  }}
                >
                  <ArrowBackIcon />
                  Back to Catalogue
                </Button> */}
                <Divider
                  orientation="vertical"
                  sx={{
                    display: { xs: "none", sm: "block" },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: "10px",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "700",
                      color: "#020817",
                      fontSize: {
                        xs: "18px",
                        sm: "22px",
                        md: "28px",
                      },
                    }}
                  >
                    {api?.name}
                  </Typography>
                  <Typography
                    sx={{
                      marginTop: "10px",
                      fontSize: "14px",
                      color: "#65758B",
                      fontWeight: "400",
                    }}
                  >
                    {api?.description}
                  </Typography>
                </Box>
              </Box>
              {isEmailMatch && (
                <Box
                  sx={{ display: "flex", flexDirection: "row", gap: "15px" }}
                >
                  {file && (jsonData || image?.masked) && (
                    <Tooltip title="Add to Vault">
                      <AddBoxIcon
                        color="primary"
                        sx={{ fontSize: 24, cursor: "pointer" }}
                        onClick={handleAddClick}
                      />
                    </Tooltip>
                  )}

                  <Tooltip title="Show Vault">
                    <WalletIcon
                      color="primary"
                      sx={{ fontSize: 24, cursor: "pointer" }}
                      onClick={handleVaultClick}
                    />
                  </Tooltip>
                </Box>
              )}
            </Stack>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
              <Box sx={{ minWidth: 200, marginRight: 'auto' }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Select API Key:
                </Typography>
                <SelectDropdown
                  menuOptions={apiCredList.map(cred => ({
                    label: cred.apiAccessKeysName || `API Key ${cred.id}`,
                    value: cred.id.toString()
                  }))}
                  value={selectedApiKey}
                  onChange={(value) => {
                    setSelectedApiKey(value);
                    setStatus(null);
                    setFile(null);
                  }}
                  widthSize="200px"
                  placeholder="Auto-select"
                />
              </Box>
              {api?.pricingPerPage !== null && (
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.textColor.blue,
                    background: theme.palette.background.light,
                    borderRadius: 2,
                    px: '12px',
                    py: '2px',
                    marginRight: '3px'
                  }}
                >
                  {creditPerAadhaar}
                </Typography>
              )}
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.textColor.blue,
                  background: theme.palette.background.light,
                  borderRadius: 2,
                  px: "12px",
                  py: "2px",
                }}
              >
                {creditText}
              </Typography>
            </Box>
            <Divider />
            <Combinator
              queryParamsFlag={queryParamsFlag}
              apiInput={api?.apiInput}
              jsonData={jsonData}
              setJsonData={setJsonData}
              apiResponse={api?.apiResponse}
              apiUrl={api?.apiUrl}
              file={file}
              status={status}
              setStatus={setStatus}
              setFile={setFile}
              image={image}
              setImage={setImage}
              linkedApis={api?.linkedApis}
              selectedAPI={selectedAPI}
              selectedCategory={selectedCategory}
              apiCategoryAndList={apiCategoryAndList}
              api={api}
            />
          </Paper>
          <AddToVaultDialog
            open={openDialog}
            handleClose={handleDialogClose}
            handleConfirm={handleAddConfirm}
          />
        </>
      )}
    </Wrapper>
  );
}
